// src/Redux/Slice/Patient/Booking.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../../API/EndPoints";
import AxiosInstance from "../../API/AxiosInstance";

// ------------------ ASYNC THUNKS ------------------

// Create booking by patient
export const createBooking = createAsyncThunk(
  "booking/createBooking",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        API_ENDPOINTS.BOOK_APPOINTMENT_BY_PATIENT,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Create booking by receptionist
export const createBookingByRecp = createAsyncThunk(
  "booking/createBookingByRecp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        API_ENDPOINTS.RECEPTIONIST_BOOKING,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Fetch bookings for receptionist
export const fetchBookingByRecp = createAsyncThunk(
  "booking/fetchBookingByRecp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(API_ENDPOINTS.GET_BOOKING, {
        params: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

export const searchBookingByRecp = createAsyncThunk(
  "booking/searchBookingByRecp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(API_ENDPOINTS.SEARCH_BOOKING, {
        params: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Reschedule booking by receptionist
export const rescheduleBookingByRecp = createAsyncThunk(
  "booking/rescheduleBookingByRecp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        API_ENDPOINTS.RECEP_RESCHEDULE,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// Cancel booking by receptionist
export const cancelBookingByRecp = createAsyncThunk(
  "booking/cancelBookingByRecp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        API_ENDPOINTS.RECEP_CANCEL,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong"
      );
    }
  }
);

// ------------------ INITIAL STATE ------------------
const initialState = {
  bookingData: { loading: false, error: null, data: null },
  recepData: { loading: false, error: null, data: null },
  fetchrecepData: { loading: false, error: null, data: null },
  searchrecepData: { loading: false, error: null, data: null },
  rescheduleData: { loading: false, error: null, data: null },
  cancelData: { loading: false, error: null, data: null },
};

// ------------------ SLICE ------------------
const BookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ---------------- CREATE BOOKING ----------------
    builder
      .addCase(createBooking.pending, (state) => {
        state.bookingData.loading = true;
        state.bookingData.error = null;
      })
      .addCase(createBooking.fulfilled, (state, action) => {
        state.bookingData.loading = false;
        state.bookingData.data = action.payload;
      })
      .addCase(createBooking.rejected, (state, action) => {
        state.bookingData.loading = false;
        state.bookingData.error = action.payload;
      });

    // ----------- CREATE BOOKING BY RECP -----------
    builder
      .addCase(createBookingByRecp.pending, (state) => {
        state.recepData.loading = true;
        state.recepData.error = null;
      })
      .addCase(createBookingByRecp.fulfilled, (state, action) => {
        state.recepData.loading = false;
        state.recepData.data = action.payload;
      })
      .addCase(createBookingByRecp.rejected, (state, action) => {
        state.recepData.loading = false;
        state.recepData.error = action.payload;
      });

    // ------------- FETCH BOOKINGS ---------------
    builder
      .addCase(fetchBookingByRecp.pending, (state) => {
        state.fetchrecepData.loading = true;
        state.fetchrecepData.error = null;
      })
      .addCase(fetchBookingByRecp.fulfilled, (state, action) => {
        state.fetchrecepData.loading = false;
        state.fetchrecepData.data = action.payload;
      })
      .addCase(fetchBookingByRecp.rejected, (state, action) => {
        state.fetchrecepData.loading = false;
        state.fetchrecepData.error = action.payload;
      });

    builder
      .addCase(searchBookingByRecp.pending, (state) => {
        state.searchrecepData.loading = true;
        state.searchrecepData.error = null;
      })
      .addCase(searchBookingByRecp.fulfilled, (state, action) => {
        state.searchrecepData.loading = false;
        state.searchrecepData.data = action.payload;
      })
      .addCase(searchBookingByRecp.rejected, (state, action) => {
        state.searchrecepData.loading = false;
        state.searchrecepData.error = action.payload;
      });

    // ------------- RESCHEDULE BOOKING -------------
    builder
      .addCase(rescheduleBookingByRecp.pending, (state) => {
        state.rescheduleData.loading = true;
        state.rescheduleData.error = null;
      })
      .addCase(rescheduleBookingByRecp.fulfilled, (state, action) => {
        state.rescheduleData.loading = false;
        state.rescheduleData.data = action.payload;
      })
      .addCase(rescheduleBookingByRecp.rejected, (state, action) => {
        state.rescheduleData.loading = false;
        state.rescheduleData.error = action.payload;
      });

    // ------------- CANCEL BOOKING -------------
    builder
      .addCase(cancelBookingByRecp.pending, (state) => {
        state.cancelData.loading = true;
        state.cancelData.error = null;
      })
      .addCase(cancelBookingByRecp.fulfilled, (state, action) => {
        state.cancelData.loading = false;
        state.cancelData.data = action.payload;
      })
      .addCase(cancelBookingByRecp.rejected, (state, action) => {
        state.cancelData.loading = false;
        state.cancelData.error = action.payload;
      });
  },
});

export default BookingSlice.reducer;
